import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldAlert, Plus, Pencil, Trash2, Check, X, Sparkles, Loader2 } from 'lucide-react';
import {
  reachBlockersAPI, ReachBlocker, BlockerType, BlockerStatus
} from '../../services/reachService';

const TYPE_LABELS: Record<BlockerType, string> = {
  individual: 'Individual',
  team: 'Team',
  structural: 'Structural',
};
const TYPE_COLORS: Record<BlockerType, string> = {
  individual: 'bg-blue-50 text-blue-700 border-blue-200',
  team: 'bg-violet-50 text-violet-700 border-violet-200',
  structural: 'bg-red-50 text-red-700 border-red-200',
};
const STATUS_LABELS: Record<BlockerStatus, string> = {
  open: 'Open',
  'in-progress': 'In Progress',
  resolved: 'Resolved',
};
const STATUS_COLORS: Record<BlockerStatus, string> = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  'in-progress': 'bg-blue-50 text-blue-700 border-blue-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
};

type FormData = Omit<ReachBlocker, 'id' | 'createdAt' | 'updatedAt' | 'aiSuggested' | 'confirmedByPractitioner'>;

const EMPTY: FormData = {
  type: 'individual', description: '', raisedInWeek: 1,
  status: 'open', action: '', owner: '', dueDate: '',
};

interface FormModalProps {
  initial?: ReachBlocker | null;
  onSave: (data: FormData) => Promise<void>;
  onClose: () => void;
}

const FormModal: React.FC<FormModalProps> = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState<FormData>(initial ? {
    type: initial.type, description: initial.description, raisedInWeek: initial.raisedInWeek,
    status: initial.status, action: initial.action ?? '', owner: initial.owner ?? '',
    dueDate: initial.dueDate ?? '',
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof FormData, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.description.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <h2 className="text-base font-bold text-navy">{initial ? 'Edit Blocker' : 'New Blocker'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Description <span className="text-red-500">*</span></label>
            <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Describe the blocker clearly…"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Type</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(['individual', 'team', 'structural'] as BlockerType[]).map(t => (
                <button key={t} type="button" onClick={() => set('type', t)}
                  className={`flex-1 py-2 text-xs font-semibold transition-all ${
                    form.type === t ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}>
                  {TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Status</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(['open', 'in-progress', 'resolved'] as BlockerStatus[]).map(s => (
                <button key={s} type="button" onClick={() => set('status', s)}
                  className={`flex-1 py-2 text-xs font-semibold transition-all ${
                    form.status === s ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}>
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Raised in week</label>
              <input type="number" min={1} max={12} value={form.raisedInWeek} onChange={e => set('raisedInWeek', parseInt(e.target.value, 10))}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Due date</label>
              <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Owner</label>
            <input value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="Name or role"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Action</label>
            <textarea rows={2} value={form.action} onChange={e => set('action', e.target.value)}
              placeholder="What needs to happen to resolve this?"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
          <button onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !form.description.trim()}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60">
            <Check size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const BlockersPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [blockers, setBlockers] = useState<ReachBlocker[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'new' | ReachBlocker | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestDone, setSuggestDone] = useState(false);
  const [statusFilter, setStatusFilter] = useState<BlockerStatus | 'all'>('all');

  useEffect(() => {
    if (!projectId) return;
    reachBlockersAPI.list(projectId).then(r => {
      setBlockers(r.data.blockers);
      setLoading(false);
    });
  }, [projectId]);

  const filtered = statusFilter === 'all' ? blockers : blockers.filter(b => b.status === statusFilter);
  const sorted = [...filtered].sort((a, b) => a.raisedInWeek - b.raisedInWeek);

  const create = async (data: FormData) => {
    if (!projectId) return;
    const r = await reachBlockersAPI.create(projectId, data);
    setBlockers(prev => [...prev, r.data.blocker]);
    setModal(null);
  };

  const update = async (id: string, data: FormData) => {
    if (!projectId) return;
    const r = await reachBlockersAPI.update(projectId, id, data);
    setBlockers(prev => prev.map(b => b.id === id ? r.data.blocker : b));
    setModal(null);
  };

  const remove = async (id: string) => {
    if (!projectId || !window.confirm('Delete this blocker?')) return;
    await reachBlockersAPI.delete(projectId, id);
    setBlockers(prev => prev.filter(b => b.id !== id));
  };

  const triggerSuggest = async () => {
    if (!projectId) return;
    setSuggesting(true);
    try {
      await reachBlockersAPI.suggest(projectId);
      setSuggestDone(true);
      setTimeout(() => setSuggestDone(false), 5000);
    } finally {
      setSuggesting(false);
    }
  };

  if (!projectId) return null;

  const openCount = blockers.filter(b => b.status === 'open').length;

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <ShieldAlert size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Blockers</h1>
            <p className="text-slate-500 text-sm font-medium">
              {openCount} open · {blockers.length} total
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={triggerSuggest}
              disabled={suggesting || suggestDone}
              className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60"
            >
              {suggesting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} className="text-violet-500" />}
              {suggestDone ? 'Analysis queued!' : suggesting ? 'Queuing…' : 'Get AI suggestions'}
            </button>
            <button onClick={() => setModal('new')}
              className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
              <Plus size={16} /> Add blocker
            </button>
          </div>
        </header>

        {suggestDone && (
          <div className="flex items-center gap-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
            <Sparkles size={16} className="text-violet-500 flex-shrink-0" />
            <p className="text-sm text-violet-700">
              AI analysis queued. Suggested blockers will appear here once the workflow completes (usually within a minute).
            </p>
          </div>
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0] flex items-center gap-3 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Status:</span>
            {(['all', 'open', 'in-progress', 'resolved'] as const).map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  statusFilter === s ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}>
                {s === 'all' ? 'All' : STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <ShieldAlert size={28} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No blockers recorded.</p>
              <p className="text-slate-400 text-xs mt-1">Add blockers manually or use AI suggestions from pulse and tracker data.</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-[#e2e8f0]">
              {sorted.map(b => (
                <div key={b.id} className="px-4 py-4 flex items-start gap-4 hover:bg-slate-50/50 transition-colors">
                  <div className="flex flex-col gap-1.5 flex-shrink-0 pt-0.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${TYPE_COLORS[b.type]}`}>
                      {TYPE_LABELS[b.type]}
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLORS[b.status]}`}>
                      {STATUS_LABELS[b.status]}
                    </span>
                    {b.aiSuggested && (
                      <span className="flex items-center gap-1 text-[10px] text-violet-500 font-semibold">
                        <Sparkles size={10} /> AI
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy leading-snug">{b.description}</p>
                    {b.action && <p className="text-xs text-slate-500 mt-1">Action: {b.action}</p>}
                    <div className="flex items-center gap-3 mt-1.5">
                      {b.owner && <span className="text-xs text-slate-400">Owner: {b.owner}</span>}
                      {b.dueDate && <span className="text-xs text-slate-400">Due: {b.dueDate}</span>}
                      <span className="text-xs text-slate-400">Week {b.raisedInWeek}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setModal(b)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => remove(b.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal === 'new' && (
        <FormModal onSave={create} onClose={() => setModal(null)} />
      )}
      {modal && modal !== 'new' && (
        <FormModal initial={modal} onSave={d => update(modal.id, d)} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

export default BlockersPage;
